import { pdf } from '@react-pdf/renderer';
import { captureException } from '@sentry/react';
import JSZip from 'jszip';
import PDFMerger from 'pdf-merger-js';
import { downloadFile, fetchFile } from 'further-ui/utils';
import { TaxReport } from 'further-ui/components';
import api from 'lib/trpcClient';
import { useNotification } from 'hooks/ui/useNotification';
import { Api } from 'further-types/annual-tax-report';

export const BULK_DOWNLOAD_LIMIT = 100;

const generatePdfForReport = async (report: Api.GetReportResponse) => {
  const pdfMerger = new PDFMerger();

  const pdfBlob = await pdf(
    <TaxReport
      reportComponents={report.components}
      firmLogoUrl={report.components.firmLogoUrl}
    />,
  ).toBlob();
  await pdfMerger.add(await pdfBlob.arrayBuffer());

  const certificates = (
    await Promise.allSettled(
      report.certificates.map((url) => (url ? fetchFile(url) : null)),
    )
  )
    .filter(
      (result): result is PromiseFulfilledResult<ArrayBuffer> =>
        result.status === 'fulfilled' && result.value !== null,
    )
    .map((result) => result.value);

  for (const certificate of certificates) {
    try {
      await pdfMerger.add(certificate);
    } catch (e) {
      captureException(e);
    }
  }

  return pdfMerger.saveAsBuffer();
};

export function useBulkDownloadTaxReports() {
  const notification = useNotification();

  return api.annualTaxReport.getBulkTaxReports.useMutation({
    onSuccess: async (reports, { taxYearStart }) => {
      try {
        if (!reports?.length) {
          throw new Error('No reports returned');
        }

        const zip = new JSZip();

        for (const report of reports) {
          try {
            const pdfBuffer = await generatePdfForReport(report);
            const taxYear = report.components.taxYear.split('/').join('-');
            const filename = `${report.components.investorFullName} (${report.investorId}) - Annual tax report ${taxYear}.pdf`;
            zip.file(filename, pdfBuffer);
          } catch (e) {
            captureException(e);
          }
        }

        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const taxYear = `${taxYearStart}-${taxYearStart + 1}`;
        downloadFile(zipBlob, `Tax reports ${taxYear}.zip`);

        notification.success('Tax reports downloaded successfully.');
      } catch (error) {
        captureException(error);
        notification.error('Unable to download tax reports. Please try again.');
      }
    },
    onError: (error) => {
      captureException(error);
      notification.error('Unable to download tax reports. Please try again.');
    },
  });
}
